import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { PrintFormat } from '../../shared/models/sale/print-format.enum';
import { IReceiptBySaleMovement } from '../../shared/models/reception/receipt-by-sale-movement';
import { ISendReceiptByEmail } from '../../shared/models/sale/send-receipt-by-email';
import { IReceiptType } from '../../shared/models/reception/receipt-type';
import { IPagedResult } from '../../shared/models/paged-result';
import { DownloadReceiptFilterSet } from '../../shared/models/reception/receipt-filter-set';

type CancellationDocumentType = 'ra' | 'rc';

const VALID_DOCUMENT_TYPES = ['01', '03', '07', '08', '09']; // Factura, Boleta, Nota de Crédito, Nota de Débito

const toReceiptDownloadQueryString = (filterSet: DownloadReceiptFilterSet): string => {
  const params = new URLSearchParams();

  Object.entries(filterSet).forEach(([key, value]) => {
    params.set(key, String(value ?? ''));
  });

  return params.toString();
};

export const receiptApi = createApi({
  reducerPath: 'receiptApi',
  baseQuery,
  endpoints: (builder) => ({
    getReceiptTypes: builder.query<IPagedResult<IReceiptType>, void>({
      query: () => ({
        url: '/api/receiptTypes?size=-1',
        method: 'GET',
      }),
      transformResponse: (response: IPagedResult<IReceiptType>) => ({
        ...response,
        results: response.results.filter((doc) => VALID_DOCUMENT_TYPES.includes(doc.sunatCode)),
      }),
    }),
    getParentReceiptTypes: builder.query<IPagedResult<IReceiptType>, void>({
      query: () => ({
        url: '/api/receiptTypes?size=-1',
        method: 'GET',
      }),
      transformResponse: (response: IPagedResult<IReceiptType>) => ({
        ...response,
        results: response.results.filter((doc) => ['01', '03'].includes(doc.sunatCode)),
      }),
    }),
    receiptList: builder.query<IReceiptBySaleMovement[], { saleMovementId: string }>({
      query: ({ saleMovementId }) => ({
        url: `/api/receipts/salemovement/${saleMovementId}/list`,
        method: 'GET',
      }),
    }),
    printTicketPdf: builder.query<
      Blob,
      { id: string; printFormat?: PrintFormat; paymentCondition: string }
    >({
      query: ({ id, printFormat, paymentCondition }) => {
        const randomParam = Date.now();
        let url = `/api/receipts/${id}/views/pdf/${printFormat ?? PrintFormat.A4}?_=${randomParam}`;
        if (paymentCondition) {
          url += `&paymentCondition=${encodeURIComponent(paymentCondition)}`;
        }
        return {
          url,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    printCancellationPdf: builder.query<Blob, { id: string; type: CancellationDocumentType }>({
      query: ({ id, type }) => {
        const randomParam = Date.now();
        const controller = type === 'ra' ? 'voideddocuments' : 'summarydocuments';
        return {
          url: `/api/${controller}/${id}/views/pdf?_=${randomParam}`,
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
          },
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    downloadReceiptZip: builder.mutation<Blob, DownloadReceiptFilterSet>({
      query: (filterSet) => ({
        url: `/api/receipts/download/zip?${toReceiptDownloadQueryString(filterSet)}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    sendReceiptByEmail: builder.mutation<void, ISendReceiptByEmail>({
      query: (body) => ({
        url: `/api/receipts/send-receipt`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetReceiptTypesQuery,
  useGetParentReceiptTypesQuery,
  useReceiptListQuery,
  useLazyReceiptListQuery,
  useLazyPrintTicketPdfQuery,
  useLazyPrintCancellationPdfQuery,
  useDownloadReceiptZipMutation,
  useSendReceiptByEmailMutation,
} = receiptApi;
